import React, { useContext, useState } from 'react';
import { CountryCode } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { BookingPerson, getSpecialistsByName } from '@components/dashboard/data/Specialists';
import { BodyBold } from '@components/dashboard/Typography';
import AddToCart from '@components/shared/AddToCart';
import Colors from '@components/shared/Colors';
import ReplacedText from '@components/shared/ReplacedText';
import { BodySM, BodySMBold } from '@components/shared/TextTypes';
import { CartItemType, DigitalServiceType } from '@contracts';
import IconArrow from '@images/icon-arrow.svg';
import MoneyIcon from '@images/vms-account/vms-icons/Money.svg';
import TimeIcon from '@images/vms-account/vms-icons/Time.svg';
import { createSimpleCartItemForProductCode } from '@utils/cart';

interface Props {}

const NurseConsultationModule: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { currency, country } = state;
  const specialistName = country === CountryCode.US ? 'Marsha Kentish' : 'Sarah Montagu';
  const specialistsData = getSpecialistsByName(DigitalServiceType.VMS, [specialistName], country);
  const [showDropdown, setShowDropdown] = useState(false);

  function handleSetShowDropdown() {
    setShowDropdown(!showDropdown);
  }

  const renderTimePriceBar = (item: BookingPerson) => {
    const getPriceString = (priceObj) =>
      typeof priceObj === 'string' ? priceObj : priceObj?.find((p) => p.currency === currency.letterCode)?.amount;
    const priceValue = getPriceString(item.price);
    const displayPrice = priceValue?.split('.') ?? ['0', '00'];
    const quantity = 1;
    const editable = true;
    const cartItem =
      Number(priceValue) > -1
        ? createSimpleCartItemForProductCode(item.productCode, quantity, Number(priceValue), editable, CartItemType.Consultation)
        : null;

    return (
      <BottomBar>
        <TimePriceWrap>
          <DetailWrap>
            <TimeIcon />
            <BodyBold>{item.time}</BodyBold>
          </DetailWrap>
          <DetailWrap>
            <MoneyIcon />
            <Price>
              {currency.symbol}
              {displayPrice[0]}
              <sup>{displayPrice[1]}</sup>
            </Price>
          </DetailWrap>
        </TimePriceWrap>
        <AddToCart cartItem={cartItem} />
      </BottomBar>
    );
  };

  const renderIconBox = (item: BookingPerson) => (
    <IconBox key={item.name}>
      <HeaderBar onClick={handleSetShowDropdown}>
        <StyledSmallIcon src={item.picture} alt={item.name} />
        <Text>
          <BodySMBold>
            {country === CountryCode.US ? t('nurse-consultation-module.book-intro-us') : t('nurse-consultation-module.book-intro')}
          </BodySMBold>
          <NameTitleWrap>
            <ReplacedText as={BodySM}>{item.name}</ReplacedText>
            <ReplacedText as={LightText}>{item.shortTitle}</ReplacedText>
          </NameTitleWrap>
        </Text>
        <IconArrowStyled $open={showDropdown} />
      </HeaderBar>
      <Dropdown $open={showDropdown}>
        <DropdownInner>
          <DescriptionWrap>
            <ReplacedText as={BodySM}>{item.bio}</ReplacedText>
          </DescriptionWrap>
        </DropdownInner>
      </Dropdown>
      {renderTimePriceBar(item)}
    </IconBox>
  );

  return <Container>{specialistsData.map((item) => item && renderIconBox(item))}</Container>;
};

export default NurseConsultationModule;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const IconBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  background: ${Colors.cream};
  width: 100%;
  border-radius: 2px;
  border: 1px solid ${Colors.paleGold50};
`;

const HeaderBar = styled.div`
  display: flex;
  padding: 15px 20px;
  align-items: center;
  gap: 15px;
  justify-content: space-between;
  width: 100%;
  background: ${Colors.ecru50};
  cursor: pointer;
`;

const StyledSmallIcon = styled.img`
  width: 60px;
  height: 60px;
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex-grow: 1;
`;

const NameTitleWrap = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;

  span {
    text-align: left;

    @media (max-width: 1024px) {
      font-size: 13px;
    }
  }

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 0;
  }
`;

const IconArrowStyled = styled(IconArrow)`
  width: 12px;
  height: 7px;
  margin-left: 10px;
  transition: transform 0.2s ease-out;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : null)};
`;

const Dropdown = styled.div<{ $open?: boolean }>`
  display: flex;
  transition: height 200ms ease-in-out;
  height: ${({ $open }) => ($open ? 'auto' : 0)};
  overflow: hidden;
`;

const DropdownInner = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${Colors.paleGold50};
`;

const DescriptionWrap = styled.div`
  display: flex;
  padding: 15px 20px;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

const BottomBar = styled.div`
  display: flex;
  padding: 10px 20px;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  align-self: stretch;
  border-top: 1px solid ${Colors.paleGold50};
`;

const TimePriceWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  align-self: stretch;

  & button {
    height: 44px;
    padding: 0px 15px;
  }

  @media (max-width: 1024px) {
    gap: 15px;
  }
`;

const DetailWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;

  p {
    white-space: nowrap;
  }
`;

const Price = styled(BodyBold)`
  display: flex;
  align-items: center;
  text-align: center;
  color: ${Colors.navyBlue};
  font-family: 'Domaine';
  font-size: 18px;

  sup {
    top: -2px;
    margin-left: 2px;
    font-size: 65%;
  }
`;

const StyledBodySM = styled(BodySM)`
  @media (max-width: 1024px) {
    text-align: center;
  }
`;

const LightText = styled(StyledBodySM)`
  opacity: 0.5;
`;
